import type { BabyEvent, EventType } from '@bb/shared/core';
import { api } from './api';
import { platform } from './platform';
import type { AppCtx } from './state';

/** Запуск таймера «прямо сейчас». */
export async function startTimer(ctx: AppCtx, type: EventType, payload: Record<string, unknown> = {}) {
  try {
    await api.createEvent(ctx.child.id, { type, startedAt: new Date().toISOString(), payload });
    platform.haptic('success');
    ctx.bump();
  } catch (e) {
    ctx.fail(e);
  }
}

export async function stopTimer(ctx: AppCtx, e: BabyEvent) {
  try {
    await api.stopEvent(e.id);
    platform.haptic('success');
    ctx.bump();
  } catch (err) {
    ctx.fail(err);
  }
}

/** «Сменить грудь»: закрываем текущее кормление и сразу начинаем другую сторону. */
export async function switchSide(ctx: AppCtx, e: BabyEvent) {
  const next = e.payload.side === 'left' ? 'right' : 'left';
  try {
    await api.stopEvent(e.id);
    await api.createEvent(ctx.child.id, { type: 'breast', startedAt: new Date().toISOString(), payload: { side: next } });
    platform.haptic('light');
  } catch (err) {
    ctx.fail(err);
  }
  ctx.bump();
}

/** Удаление с возможностью вернуть из тоста. */
export async function deleteWithUndo(ctx: AppCtx, e: BabyEvent) {
  try {
    await api.deleteEvent(e.id);
    platform.haptic('warning');
    ctx.bump();
    ctx.toast({
      text: 'Запись удалена',
      action: {
        label: 'Вернуть',
        run: () => {
          api.restoreEvent(e.id).then(() => ctx.bump(), ctx.fail);
        },
      },
    });
  } catch (err) {
    ctx.fail(err);
  }
}

/* ---------- Кормление грудью: отрезки по сторонам, пауза, смена, будильник ---------- */

type Side = 'left' | 'right';
interface Segment {
  side: Side;
  from: string;
  to: string | null;
}

/** Отрезки идущего кормления (старые записи без отрезков — один отрезок от начала). */
export function segmentsOf(e: BabyEvent): Segment[] {
  const s = e.payload.segments as Segment[] | undefined;
  if (s?.length) return s;
  const side: Side = e.payload.side === 'right' ? 'right' : 'left';
  return [{ side, from: e.startedAt, to: e.payload.pausedAt ? String(e.payload.pausedAt) : null }];
}

export const isPaused = (e: BabyEvent) => Boolean(e.payload.pausedAt);

/** Подходы: подряд идущие отрезки одной стороны (пауза не прерывает подход, смена груди — начинает новый). */
export function runsOf(e: BabyEvent, now = Date.now()): { side: Side; sec: number }[] {
  const out: { side: Side; sec: number }[] = [];
  for (const g of segmentsOf(e)) {
    const sec = Math.max(0, ((g.to ? Date.parse(g.to) : e.payload.pausedAt ? Date.parse(String(e.payload.pausedAt)) : now) - Date.parse(g.from)) / 1000);
    const last = out[out.length - 1];
    if (last && last.side === g.side) last.sec += sec;
    else out.push({ side: g.side, sec });
  }
  return out;
}

/** Секунды текущего подхода (крупный таймер на карточке). */
export const currentRunSec = (e: BabyEvent, now = Date.now()) => runsOf(e, now).at(-1)?.sec ?? 0;

/** Сигнал таймера груди: начало текущего подхода + цель (с учётом пауз — считаем от «сейчас»). */
function breastAlarm(e: BabyEvent, target: number | null | undefined, runSec: number, nowMs: number) {
  if (!target) return { alarmAt: null, alarmFrom: null, sideTargetSec: null };
  return {
    sideTargetSec: target,
    alarmAt: new Date(nowMs + Math.max(0, target - runSec) * 1000).toISOString(),
    alarmFrom: new Date(nowMs - runSec * 1000).toISOString(),
  };
}
export const currentSide = (e: BabyEvent): Side => segmentsOf(e).at(-1)!.side;
const otherSide = (s: Side): Side => (s === 'left' ? 'right' : 'left');
/** Момент напоминания через sec секунд (null — без напоминания). */
export const alarmIso = (sec: number | null) => (sec ? new Date(Date.now() + sec * 1000).toISOString() : null);

async function patchPayload(ctx: AppCtx, e: BabyEvent, payload: Record<string, unknown>, haptic: 'light' | 'success' = 'light') {
  try {
    await api.updateEvent(e.id, { payload });
    platform.haptic(haptic);
    ctx.bump();
  } catch (err) {
    ctx.fail(err);
  }
}

export async function startBreast(ctx: AppCtx, side: Side, sideTargetSec: number | null) {
  const now = new Date().toISOString();
  await startTimer(ctx, 'breast', {
    side,
    segments: [{ side, from: now, to: null }],
    ...(sideTargetSec ? { sideTargetSec, alarmAt: alarmIso(sideTargetSec), alarmFrom: now } : {}),
  });
}

/** Таймер на грудь: «всего 7 минут на эту сторону» — уже прошедшее время засчитывается. */
export async function setSideTarget(ctx: AppCtx, e: BabyEvent, target: number | null) {
  const nowMs = Date.now();
  await patchPayload(ctx, e, { ...e.payload, ...breastAlarm(e, target, currentRunSec(e, nowMs), nowMs) });
  if (target) ctx.toast({ text: `Таймер на грудь: ${Math.floor(target / 60)}:${String(target % 60).padStart(2, '0')}` });
}

export async function pauseBreast(ctx: AppCtx, e: BabyEvent) {
  const now = new Date().toISOString();
  const segments = segmentsOf(e).map((g) => (g.to ? g : { ...g, to: now }));
  await patchPayload(ctx, e, { ...e.payload, segments, pausedAt: now });
}

export async function resumeBreast(ctx: AppCtx, e: BabyEvent, side?: Side) {
  const nowMs = Date.now();
  const now = new Date(nowMs).toISOString();
  const nextSide = side ?? currentSide(e);
  const switching = nextSide !== currentSide(e);
  const segments = [...segmentsOf(e).map((g) => (g.to ? g : { ...g, to: now })), { side: nextSide, from: now, to: null }];
  const target = e.payload.sideTargetSec as number | null | undefined;
  let alarm: Record<string, unknown> = {};
  if (target) {
    // Новая грудь — отсчёт заново; та же грудь после паузы — продолжаем с того же места.
    alarm = breastAlarm(e, target, switching ? 0 : currentRunSec(e, nowMs), nowMs);
  } else if (e.payload.alarmAt && e.payload.pausedAt) {
    const shift = nowMs - Date.parse(String(e.payload.pausedAt));
    alarm = { alarmAt: new Date(Date.parse(String(e.payload.alarmAt)) + shift).toISOString() };
  }
  await patchPayload(ctx, e, { ...e.payload, segments, pausedAt: null, ...alarm });
}

/** Сменить грудь: текущий отрезок закрывается, другой начинается сразу (с паузы — тоже продолжаем). */
export async function switchBreast(ctx: AppCtx, e: BabyEvent) {
  await resumeBreast(ctx, e, otherSide(currentSide(e)));
}

export async function setAlarm(ctx: AppCtx, e: BabyEvent, secondsFromNow: number | null, label?: string) {
  await patchPayload(ctx, e, { ...e.payload, alarmAt: alarmIso(secondsFromNow), alarmFrom: secondsFromNow ? new Date().toISOString() : null });
  if (secondsFromNow) ctx.toast({ text: `Таймер на ${label ?? `${Math.round(secondsFromNow / 60)} мин`} запущен` });
}

/* ---------- Бутылочка: намешали → таймер → сколько осталось ---------- */

export async function startBottle(ctx: AppCtx, preparedMl: number, kind: string, alarmSec: number | null) {
  await startTimer(ctx, 'bottle', { ml: preparedMl, preparedMl, kind, alarmAt: alarmIso(alarmSec), alarmFrom: alarmSec ? new Date().toISOString() : null });
}

export async function finishBottle(ctx: AppCtx, e: BabyEvent, leftMl: number) {
  const prepared = Number(e.payload.preparedMl ?? e.payload.ml) || 0;
  const left = Math.max(0, Math.min(prepared, leftMl));
  try {
    await api.updateEvent(e.id, {
      endedAt: new Date().toISOString(),
      payload: { ...e.payload, ml: prepared - left, preparedMl: prepared, leftMl: left, alarmAt: null },
    });
    platform.haptic('success');
    ctx.toast({ text: `Записано: выпил ${prepared - left} мл` });
    ctx.bump();
  } catch (err) {
    ctx.fail(err);
  }
}

/* ---------- Сон: «начали укладывать» → «уснул» ---------- */

export async function startSettling(ctx: AppCtx, details: Record<string, unknown>) {
  const now = new Date().toISOString();
  await startTimer(ctx, 'sleep', { ...details, settling: true, settleStartedAt: now });
}

/** Уснул: сон начинается сейчас, время укладывания остаётся в записи. */
export async function fellAsleep(ctx: AppCtx, e: BabyEvent) {
  const now = new Date().toISOString();
  const { settling: _s, ...rest } = e.payload;
  try {
    await api.updateEvent(e.id, { startedAt: now, payload: { ...rest, settleStartedAt: String(e.payload.settleStartedAt ?? e.startedAt) } });
    platform.haptic('success');
    ctx.toast({ text: 'Уснул — засекаем сон' });
    ctx.bump();
  } catch (err) {
    ctx.fail(err);
  }
}

export async function toggleMethod(ctx: AppCtx, e: BabyEvent, m: string) {
  const list = Array.isArray(e.payload.methods) ? (e.payload.methods as string[]) : [];
  await patchPayload(ctx, e, { ...e.payload, methods: list.includes(m) ? list.filter((x) => x !== m) : [...list, m] });
}

/** Не уснул — укладывание отменяем (запись удаляется). */
export async function cancelSettling(ctx: AppCtx, e: BabyEvent) {
  try {
    await api.deleteEvent(e.id);
    ctx.toast({ text: 'Укладывание отменено' });
    ctx.bump();
  } catch (err) {
    ctx.fail(err);
  }
}
